import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Chip,
  IconButton,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  CloudUpload,
  People,
  Inventory,
  LocalShipping,
  ContentCut,
  ArrowBack,
  CheckCircle,
  Info,
  GetApp,
  Publish,
  PersonSearch,
} from '@mui/icons-material';

const DataManagement = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  // Custom Pound Icon Component
  const PoundIcon = ({ sx }) => (
    <Box sx={{ fontSize: 40, fontWeight: 'bold', ...sx }}>£</Box>
  );

  const dataTypes = [
    {
      id: 'employees',
      title: 'Employees',
      description: 'Import staff records with personal details, payroll info, and training records',
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      importRoute: '/import/employees',
      exportRoute: '/export/employees',
      features: [
        'Personal details & contact information',
        'Employment dates & role assignments',
        'Payroll information (hourly rates, commissions)',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'products',
      title: 'Products',
      description: 'Bulk import your product catalog with pricing, stock levels, and supplier information',
      icon: <Inventory sx={{ fontSize: 40 }} />,
      color: '#E91E63',
      importRoute: '/import/products',
      exportRoute: '/export/products',
      features: [
        'Product names, barcodes & descriptions',
        'Current & average cost prices',
        'Retail prices with VAT rates',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'services',
      title: 'Services',
      description: 'Import your service menu with pricing, duration, and product requirements',
      icon: <ContentCut sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      importRoute: '/import/services',
      exportRoute: '/export/services',
      features: [
        'Service names & descriptions',
        'Duration & pricing information',
        'Product usage & quantities',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'expenditure',
      title: 'Expenditure',
      description: 'Import historical expenses and overhead costs for accurate business analysis',
      icon: <PoundIcon />,
      color: '#FF5722',
      importRoute: '/import/expenditure',
      exportRoute: '/export/expenditure',
      features: [
        'Expense dates & amounts',
        'Category classifications',
        'Supplier & payment information',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'suppliers',
      title: 'Suppliers',
      description: 'Import supplier contacts and terms of trade for efficient stock ordering',
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      importRoute: '/import/suppliers',
      exportRoute: '/export/suppliers',
      features: [
        'Supplier names & contact details',
        'Payment terms & delivery info',
        'Product associations',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'income',
      title: 'Income',
      description: 'Import daily takings with employee sales breakdown and payment methods',
      icon: <PoundIcon />,
      color: '#FFC107',
      importRoute: '/import/income',
      exportRoute: '/export/income',
      features: [
        'Daily cash, card & voucher totals',
        'Employee service & product sales',
        'Client counts per stylist',
      ],
      hasImport: true,
      hasExport: true,
    },
    {
      id: 'clients',
      title: 'Clients',
      description: 'Import client records with optional patch test history for migrating salons',
      icon: <PersonSearch sx={{ fontSize: 40 }} />,
      color: '#00897B',
      importRoute: '/import/clients',
      exportRoute: null,
      features: [
        'Client contact details & allergy notes',
        'Previous patch test records from paper',
        'Product type, brand & formula details',
      ],
      hasImport: true,
      hasExport: false,
    },
  ];

  const DataTypeCard = ({ dataType }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          borderColor: dataType.color,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 60, height: 60, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: `${dataType.color}15`, color: dataType.color,
            }}
          >
            {dataType.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">{dataType.title}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {dataType.hasImport && (
                <Chip label="Import" size="small" color="primary" variant="outlined" />
              )}
              {dataType.hasExport && (
                <Chip label="Export" size="small" color="success" variant="outlined" />
              )}
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dataType.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Includes:
        </Typography>
        <List dense sx={{ mb: 2 }}>
          {dataType.features.slice(0, 3).map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircle sx={{ fontSize: 16, color: dataType.color }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
          {dataType.hasImport && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<Publish />}
              onClick={() => navigate(dataType.importRoute)}
              sx={{ bgcolor: dataType.color, '&:hover': { bgcolor: dataType.color } }}
            >
              Import {dataType.title}
            </Button>
          )}
          {dataType.hasExport && (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<GetApp />}
              onClick={() => navigate(dataType.exportRoute)}
              sx={{ borderColor: dataType.color, color: dataType.color }}
            >
              Export {dataType.title}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <CloudUpload sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">Data Management</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="fullWidth">
            <Tab label="Overview" />
            <Tab label="Import Guidelines" />
          </Tabs>
        </Paper>

        {currentTab === 0 && (
          <Grid container spacing={3}>
            {dataTypes.map((dataType) => (
              <Grid item xs={12} md={6} lg={4} key={dataType.id}>
                <DataTypeCard dataType={dataType} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default DataManagement;